import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = (article.relatedArticles || [])
    .map((id: string) => articles.find((a) => a.id === id))
    .filter(Boolean);

  const categoryMap: Record<string, string> = {
    'japan-uncensored': '日本无码',
    'korea-uncensored': '韩国无码',
    'southeast-asia': '东南亚无码',
    'china-uncensored': '国产无码',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '亚洲无码正版内容平台推荐指南',
    },
    url: `https://shakea1.cn/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲无码正版内容平台推荐指南',
      url: 'https://shakea1.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryMap[article.category] || '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <header className="mb-8">
            <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-4">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {article.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md text-xs whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div
            className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
              prose-h2:font-heading prose-h2:text-lg prose-h2:font-semibold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-3
              prose-h3:font-heading prose-h3:text-base prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-6 prose-h3:mb-2
              prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-li:text-sm prose-li:leading-relaxed prose-li:mb-1
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <footer className="mt-12 pt-8 border-t border-background-200/70">
              <h4 className="font-heading font-semibold text-base text-foreground-900 mb-4">
                <a href="#related-articles">相关文章</a>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra!.id}
                    to={`/article/${ra!.slug}`}
                    className="p-3 bg-background-100/70 rounded-lg hover:bg-background-100 transition-colors group"
                  >
                    <h5 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra!.title}
                    </h5>
                    <p className="text-xs text-foreground-500 mt-1">{ra!.readTime}</p>
                  </Link>
                ))}
              </div>
            </footer>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}